import Vue from "vue";
import Router from "vue-router";

import store from "@/store";

// Views
import Login from "@/views/Login";
import SSO from "@/views/SSO";
import Register from "@/views/Register";
import Forgot from "@/views/Forgot";

import Home from "@/views/Home";
import Account from "@/views/Account";
import EasyAuth from "@/views/EasyAuth";
import Support from "@/views/Support";
import Cart from "@/views/Cart";
import Error404 from "@/views/Error404";
import LayoutSidebar from "@/components/LayoutSidebar";
import LayoutFull from "@/components/LayoutFull";

const RouterView = { render: c => c("router-view") };
const WithSidebar = {
  render: c => c(LayoutSidebar, [c(RouterView)])
};
const FullWidth = {
  render: c => c(LayoutFull, [c(RouterView)])
};

Vue.use(Router);

function redirectToAppPurchase() {
  const token = store.state.user.token && store.state.user.token.value;
  if (!token) return window.location.replace("https://nanome.ai/pricing");
  const url = new URL("https://app.nanome.ai/purchase");
  if (token) url.searchParams.set("auth", token);
  window.location.replace(url.toString());
}

const routes = [
  {
    path: "*",
    name: "404",
    component: Error404
  },
  {
    path: "/purchase",
    beforeEnter: redirectToAppPurchase
  },
  {
    path: "/cart",
    component: WithSidebar,
    meta: {
      title: "Your Cart",
      requiresAuth: true,
      loginText: "to view your cart"
    },
    children: [
      {
        path: "",
        name: "Cart",
        component: Cart,
        meta: { quiet: true }
      }
    ]
  },
  {
    path: "/register",
    component: FullWidth,
    children: [
      {
        path: "",
        name: "Register",
        component: Register
      },
      {
        path: "verify",
        name: "Verify",
        component: () => import("@/views/Verify")
      }
    ]
  },
  {
    path: "/login",
    component: FullWidth,
    children: [
      {
        path: "",
        name: "Login",
        component: Login
      },
      {
        path: "sso",
        name: "SSO",
        component: SSO
      },
      {
        path: "forgot",
        name: "Forgot",
        component: Forgot
      },
      {
        path: "reset",
        name: "Reset",
        component: () => import("@/views/Reset")
      }
    ]
  },
  {
    path: "/claim",
    name: "Claim",
    component: () => import("@/views/Claim")
  },
  {
    path: "/join/:b64RoomName",
    name: "Join",
    component: () => import("@/views/Join"),
    props: true
  },

  {
    path: "/",
    redirect: "/dashboard",
    component: WithSidebar,
    meta: {
      requiresAuth: true,
      title: "Home"
    },
    children: [
      {
        path: "dashboard",
        name: "Home",
        component: Home
      },
      {
        path: "account",
        name: "Account",
        component: Account
      },
      {
        path: "activate",
        name: "Activate",
        component: EasyAuth
      },
      {
        path: "plans",
        name: "Plans",
        redirect: "licenses"
      },
      {
        path: "licenses",
        component: RouterView,
        meta: { title: "Licenses" },
        children: [
          {
            path: "",
            name: "Licenses",
            component: () =>
              import(
                /* webpackChunkName: "licenses" */ "@/views/licenses/Licenses"
              )
          },
          {
            path: "add",
            name: "Add License",
            beforeEnter: redirectToAppPurchase
          },
          {
            path: "pending",
            name: "Pending Licenses",
            component: () =>
              import(
                /* webpackChunkName: "licenses" */ "@/views/licenses/LicensesPending"
              )
          }
        ]
      },
      {
        path: "billing",
        component: RouterView,
        meta: { title: "Billing" },
        children: [
          {
            path: "",
            name: "Billing",
            component: () =>
              import(
                /* webpackChunkName: "billing" */ "@/views/billing/Billing"
              )
          },
          {
            path: ":subscriptionId",
            name: "Subscription",
            component: () =>
              import(
                /* webpackChunkName: "billing" */ "@/views/billing/BillingSubscription"
              ),
            props: true
          }
        ]
      },
      {
        path: "chat",
        name: "Chat",
        meta: {
          container: false,
          quiet: true
        },
        component: () => import(/* webpackChunkName: "chat" */ "@/views/Chat")
      },
      {
        path: "contacts",
        name: "Contacts",
        component: () =>
          import(/* webpackChunkName: "contacts" */ "@/views/Contacts")
      },
      {
        path: "app-logs",
        name: "App Logs",
        component: () =>
          import(/* webpackChunkName: "logs" */ "@/views/AppLogs")
      },
      {
        path: "meetings/:payload?",
        name: "Meetings",
        component: () =>
          import(/* webpackChunkName: "meetings" */ "@/views/Meetings"),
        props: true
      },
      {
        path: "org",
        name: "Organization",
        component: () =>
          import(/* webpackChunkName: "organization" */ "@/views/Organization")
      },
      {
        path: "parental-controls",
        name: "Parental Controls",
        meta: { quiet: true },
        component: () =>
          import(
            /* webpackChunkName: "parental-controls" */ "@/views/ParentalControls"
          )
      },
      {
        path: "support",
        name: "Support",
        component: Support
      },
      {
        path: "admin",
        name: "Admin",
        component: RouterView,
        redirect: "/admin/search",
        beforeEnter: (to, from, next) => {
          if (!store.state.user.permissions.length) {
            return next({ name: "Home" });
          }
          next();
        },
        children: [
          {
            path: "audit-log",
            name: "Audit Log",
            component: () =>
              import(
                /* webpackChunkName: "admin" */ "@/views/admin/AdminAuditLog"
              )
          },
          {
            path: "create-license",
            name: "Create License",
            component: () =>
              import(
                /* webpackChunkName: "admin" */ "@/views/admin/AdminCreateLicense"
              )
          },
          {
            path: "organizations",
            name: "Organizations",
            component: () =>
              import(
                /* webpackChunkName: "admin" */ "@/views/admin/AdminManageOrgs"
              )
          },
          {
            path: "roles",
            name: "Roles & Permissions",
            component: () =>
              import(/* webpackChunkName: "admin" */ "@/views/admin/AdminRoles")
          },
          {
            path: "search",
            name: "Search Users",
            component: () =>
              import(
                /* webpackChunkName: "admin" */ "@/views/admin/AdminUserSearch"
              )
          }
        ]
      }
    ]
  },
  {
    path: "/quote",
    component: FullWidth,
    children: [
      {
        path: "",
        name: "Quote",
        component: () => import("@/views/Quote"),
        meta: {
          quiet: true
        }
      }
    ]
  }
];

const router = new Router({
  mode: "history",
  linkActiveClass: "open active",
  scrollBehavior: () => ({ y: 0 }),
  routes
});

router.beforeEach((to, from, next) => {
  if (store.state.preventNav) {
    const msg = "You have unsaved changes. Are you sure you want to leave?";
    if (!confirm(msg)) return;
    store.commit("SET_PREVENT_NAV", false);
  }

  const { user } = store.state;
  const requiresAuth = to.matched.some(route => route.meta.requiresAuth);

  if (!requiresAuth) {
    return next();
  }

  if (user.id && !user.require_password_change && !user.require_2fa) {
    return next();
  }

  // Send user to login
  const location = { name: "Login" };
  const { query, path } = to;

  if (path) {
    // Pass through details about page they came from for redirection
    const { auth } = query;
    delete query.auth;
    const fullPath = `${path}?${new URLSearchParams(query)}`;
    location.query = { auth, goto: fullPath };

    const meta = to.matched[0].meta;
    location.params = { message: meta && meta.loginText };
  }

  next(location);
});

export default router;
